// src/components/vendor/VendorPayments.tsx
// Visualização de Pagamentos do Vendedor

import React, { useState, useEffect, useCallback } from 'react';
import {
    Calendar,
    Download,
    Clock,
    CheckCircle,
    AlertCircle,
    DollarSign
} from 'lucide-react';
import './VendorPayments.css';

interface VendorPayment {
    _id: string;
    periodStart: string;
    periodEnd: string;
    totalCommissionCents: number;
    paymentDate: string;
    proofUrl: string | null;
    proofFileName: string | null;
    status: 'pending' | 'paid';
    notes?: string;
    createdAt: string;
}

interface VendorPaymentsProps {
    stats: {
        totalSales: number;
        totalCommission: number;
        availableBalance: number;
        activeCoupons: number;
        totalOrders: number;
    } | null;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const VendorPayments: React.FC<VendorPaymentsProps> = ({ stats, onShowToast }) => {
    const [payments, setPayments] = useState<VendorPayment[]>([]);
    const [loading, setLoading] = useState(true);
    const [nextPaymentDate, setNextPaymentDate] = useState<string | null>(null);

    const loadPayments = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/vendor/payments', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setPayments(data.payments || []);
            } else {
                onShowToast('Erro ao carregar pagamentos', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao carregar pagamentos', 'error');
        } finally {
            setLoading(false);
        }
    }, [onShowToast]);

    useEffect(() => {
        loadPayments();
        loadPaymentInfo();
    }, [loadPayments]);

    const loadPaymentInfo = async () => {
        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/vendor/payment-info', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setNextPaymentDate(data.nextPaymentDate);
            }
        } catch (error) {
            console.error('Erro ao carregar info de pagamento:', error);
        }
    };

    const formatCurrency = (cents: number) => {
        return `R$ ${(cents / 100).toFixed(2)}`;
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-BR');
    };

    const formatDateFull = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const getDaysUntilPayment = () => {
        if (!nextPaymentDate) return null;

        const now = new Date();
        const next = new Date(nextPaymentDate);
        const diffMs = next.getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        return diffDays;
    };

    const totalPaid = payments
        .filter(p => p.status === 'paid')
        .reduce((sum, p) => sum + p.totalCommissionCents, 0);

    const daysUntil = getDaysUntilPayment();

    if (loading) {
        return (
            <div className="vendor-payments-loading">
                <div className="spinner"></div>
                <p>Carregando pagamentos...</p>
            </div>
        );
    }

    return (
        <div className="vendor-payments">
            <div className="payments-header">
                <h2>Meus Pagamentos</h2>
                <p className="payments-subtitle">Acompanhe seus pagamentos e comissões</p>
            </div>

            {/* Cards de Resumo */}
            <div className="payments-summary">
                {/* Próximo Pagamento */}
                <div className="summary-card next-payment">
                    <div className="card-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
                        <Calendar size={24} />
                    </div>
                    <div className="card-content">
                        <span className="card-label">Próximo Pagamento</span>
                        {nextPaymentDate ? (
                            <>
                                <span className="card-value">{formatDateFull(nextPaymentDate)}</span>
                                {daysUntil !== null && (
                                    <span className="card-detail">
                                        {daysUntil === 0 ? 'Hoje' :
                                            daysUntil === 1 ? 'Amanhã' :
                                                `Em ${daysUntil} dias`}
                                    </span>
                                )}
                            </>
                        ) : (
                            <>
                                <span className="card-value not-defined">Não definido</span>
                                <span className="card-detail">Entre em contato com o admin</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Saldo Disponível */}
                <div className="summary-card available-balance">
                    <div className="card-icon" style={{ background: 'rgba(47, 113, 48, 0.1)', color: '#2F7130' }}>
                        <DollarSign size={24} />
                    </div>
                    <div className="card-content">
                        <span className="card-label">Saldo Disponível</span>
                        <span className="card-value">{formatCurrency(stats?.availableBalance || 0)}</span>
                        <span className="card-detail">Pronto para saque</span>
                    </div>
                </div>

                {/* Total Recebido */}
                <div className="summary-card total-paid">
                    <div className="card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                        <CheckCircle size={24} />
                    </div>
                    <div className="card-content">
                        <span className="card-label">Total Recebido</span>
                        <span className="card-value">{formatCurrency(totalPaid)}</span>
                        <span className="card-detail">{payments.filter(p => p.status === 'paid').length} pagamentos</span>
                    </div>
                </div>
            </div>

            {/* Info sobre período de segurança */}
            {stats && stats.availableBalance < stats.totalCommission && (
                <div className="safety-period-info">
                    <Clock size={16} />
                    <p>
                        Parte da sua comissão ainda está no período de segurança de 7 dias.
                        Após esse período, o valor ficará disponível para saque.
                    </p>
                </div>
            )}

            {/* Histórico de Pagamentos */}
            <div className="payments-history-section">
                <h3>Histórico de Pagamentos</h3>

                {payments.length === 0 ? (
                    <div className="payments-empty">
                        <AlertCircle size={48} strokeWidth={1.5} />
                        <h4>Nenhum pagamento registrado ainda</h4>
                        <p>Seus pagamentos aparecerão aqui quando forem processados pelo admin.</p>
                    </div>
                ) : (
                    <div className="payments-table-container">
                        <table className="payments-table">
                            <thead>
                                <tr>
                                    <th>Data do Pagamento</th>
                                    <th>Período</th>
                                    <th>Valor</th>
                                    <th>Status</th>
                                    <th>Comprovante</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((payment) => (
                                    <tr key={payment._id}>
                                        <td>{formatDate(payment.paymentDate)}</td>
                                        <td className="period-cell">
                                            {formatDate(payment.periodStart)} - {formatDate(payment.periodEnd)}
                                        </td>
                                        <td className="value-cell success">
                                            {formatCurrency(payment.totalCommissionCents)}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${payment.status}`}>
                                                {payment.status === 'paid' ? (
                                                    <>
                                                        <CheckCircle size={14} />
                                                        Pago
                                                    </>
                                                ) : (
                                                    <>
                                                        <Clock size={14} />
                                                        Pendente
                                                    </>
                                                )}
                                            </span>
                                        </td>
                                        <td>
                                            {payment.proofUrl ? (
                                                <a
                                                    href={`/api/admin/sales/payments/${payment._id}/proof`}
                                                    className="btn-download"
                                                    download
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        window.open(
                                                            `/api/admin/sales/payments/${payment._id}/proof`,
                                                            '_blank'
                                                        );
                                                    }}
                                                >
                                                    <Download size={14} />
                                                    Baixar
                                                </a>
                                            ) : (
                                                <span className="no-proof">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div >
    );
};

export default VendorPayments;